import type { ModuleId } from "@/router/routes";

import type { Health, StatusTone } from "../types";
import { DEMO_PLOT_AREA_M2 } from "./demo-plot";

/**
 * Sample portfolio for the mock backend: fictional projects and clients across all six disciplines. Times are
 * relative to now, so the data stays current whenever the app runs.
 */
export interface ProjectFixture {
  id: string;
  name: string;
  client: string;
  type: string;
  city: string;
  state: string;
  address?: string;
  plotAreaM2?: number;
  stageIndex: number;
  health: Health;
  next?: { title: string; inDays: number };
  team: string[];
  updatedMinsAgo: number;
  pinned?: boolean;
  archived?: boolean;
  sample?: boolean;
  attention: number;
  modules: Partial<Record<ModuleId, [label: string, tone: StatusTone]>>;
}

const DAY_MINS = 1_440;

export const FIXTURES: readonly ProjectFixture[] = [
  {
    id: "bodakdev-residence",
    name: "Bodakdev Residence",
    client: "Mehta family",
    type: "A1",
    city: "Ahmedabad",
    state: "Gujarat",
    address: "Plot 14, Judges Bungalow Road, Bodakdev",
    plotAreaM2: DEMO_PLOT_AREA_M2,
    stageIndex: 2,
    health: { state: "on_track" },
    next: { title: "Client review of plan options", inDays: 3 },
    team: ["p-asha", "p-rohan", "p-meera"],
    updatedMinsAgo: 18,
    pinned: true,
    attention: 2,
    modules: {
      site: ["Ready", "pass"],
      design: ["2 issues", "warn"],
      render: ["3", "neutral"],
      studio: ["5 open", "neutral"],
    },
  },
  {
    id: "sabarmati-heights",
    name: "Sabarmati Heights",
    client: "Shreeji Developers",
    type: "A2",
    city: "Ahmedabad",
    state: "Gujarat",
    address: "Survey 212, Motera",
    plotAreaM2: 12_400,
    stageIndex: 3,
    health: { state: "at_risk", reason: "Fire NOC drawings are 6 days late" },
    next: { title: "AutoDCR submission", inDays: 9 },
    team: ["p-rohan", "p-asha", "p-vikram", "p-kabir"],
    updatedMinsAgo: 95,
    pinned: true,
    attention: 3,
    modules: {
      site: ["Ready", "pass"],
      design: ["5 issues", "warn"],
      render: ["8", "neutral"],
      studio: ["12 open", "neutral"],
    },
  },
  {
    id: "koramangala-workplace",
    name: "Koramangala Workplace",
    client: "Finch Analytics",
    type: "I2",
    city: "Bengaluru",
    state: "Karnataka",
    address: "4th floor, 80 Feet Road, Koramangala",
    stageIndex: 2,
    health: { state: "on_track" },
    next: { title: "Test-fit sign-off", inDays: 5 },
    team: ["p-meera", "p-priya"],
    updatedMinsAgo: 240,
    attention: 1,
    modules: { design: ["Ready", "pass"], render: ["4", "neutral"], studio: ["3 open", "neutral"] },
  },
  {
    id: "bandra-apartment",
    name: "Bandra Sea-view Apartment",
    client: "R. Kapoor",
    type: "I1",
    city: "Mumbai",
    state: "Maharashtra",
    address: "Flat 1802, Carter Road, Bandra West",
    stageIndex: 3,
    health: { state: "off_track", reason: "2 milestones overdue" },
    next: { title: "Kitchen shop drawings", inDays: -2 },
    team: ["p-meera", "p-kabir"],
    updatedMinsAgo: 3 * 60,
    attention: 4,
    modules: { design: ["1 issue", "warn"], render: ["6", "neutral"], studio: ["9 open", "fail"] },
  },
  {
    id: "whitefield-township",
    name: "Whitefield Township",
    client: "Greenarc Realty",
    type: "U2",
    city: "Bengaluru",
    state: "Karnataka",
    plotAreaM2: 412_000,
    stageIndex: 1,
    health: { state: "on_track" },
    next: { title: "Land-use budget review", inDays: 12 },
    team: ["p-neha", "p-arjun", "p-asha"],
    updatedMinsAgo: 2 * DAY_MINS,
    attention: 0,
    modules: { site: ["Ready", "pass"], design: ["Ready", "pass"], studio: ["4 open", "neutral"] },
  },
  {
    id: "bhuj-plotted-layout",
    name: "Bhuj Plotted Layout",
    client: "Kutch Land Developers",
    type: "U1",
    city: "Bhuj",
    state: "Gujarat",
    plotAreaM2: 86_000,
    stageIndex: 2,
    health: { state: "on_track" },
    next: { title: "Plot schedule to client", inDays: 6 },
    team: ["p-neha", "p-kabir"],
    updatedMinsAgo: 26 * 60,
    attention: 1,
    modules: { site: ["Ready", "pass"], design: ["Ready", "pass"], studio: ["2 open", "neutral"] },
  },
  {
    id: "mula-riverfront-park",
    name: "Mula Riverfront Park",
    client: "Pune Municipal Corporation",
    type: "L4",
    city: "Pune",
    state: "Maharashtra",
    plotAreaM2: 54_000,
    stageIndex: 2,
    health: { state: "at_risk", reason: "Stage stalled for 12 working days" },
    next: { title: "Planting strategy review", inDays: 4 },
    team: ["p-arjun", "p-neha"],
    updatedMinsAgo: 12 * DAY_MINS,
    attention: 1,
    modules: {
      site: ["Ready", "pass"],
      design: ["3 issues", "warn"],
      render: ["2", "neutral"],
      studio: ["6 open", "neutral"],
    },
  },
  {
    id: "rathore-haveli",
    name: "Rathore Haveli Reuse",
    client: "Rathore Heritage Trust",
    type: "H3",
    city: "Jaipur",
    state: "Rajasthan",
    address: "Chaura Rasta, Walled City",
    stageIndex: 2,
    health: { state: "on_hold", reason: "Paused by the client until March" },
    team: ["p-priya", "p-asha"],
    updatedMinsAgo: 40 * DAY_MINS,
    attention: 0,
    modules: {
      site: ["Survey", "neutral"],
      design: ["Paused", "neutral"],
      studio: ["On hold", "neutral"],
    },
  },
  {
    id: "volt-expo-pavilion",
    name: "Volt Motors Expo Pavilion",
    client: "Volt Motors",
    type: "E1",
    city: "Greater Noida",
    state: "Uttar Pradesh",
    stageIndex: 3,
    health: { state: "on_track" },
    next: { title: "Organiser approval", inDays: 2 },
    team: ["p-kabir", "p-meera"],
    updatedMinsAgo: 45,
    attention: 2,
    modules: {
      design: ["Ready", "pass"],
      render: ["11", "neutral"],
      studio: ["4 open", "neutral"],
    },
  },
  {
    id: "kochi-boutique-hotel",
    name: "Kochi Boutique Hotel",
    client: "Backwater Hospitality",
    type: "A5",
    city: "Kochi",
    state: "Kerala",
    address: "Princess Street, Fort Kochi",
    plotAreaM2: 2_350,
    stageIndex: 1,
    health: { state: "no_schedule" },
    team: ["p-asha", "p-priya"],
    updatedMinsAgo: 5 * DAY_MINS,
    attention: 0,
    modules: {
      site: ["Ready", "pass"],
      design: ["Ready", "pass"],
      studio: ["No schedule", "neutral"],
    },
  },
  {
    id: "doon-school-campus",
    name: "Doon Valley School Campus",
    client: "Doon Valley Education Society",
    type: "A7",
    city: "Dehradun",
    state: "Uttarakhand",
    plotAreaM2: 38_000,
    stageIndex: 0,
    health: { state: "on_track" },
    next: { title: "Brief workshop", inDays: 8 },
    team: ["p-rohan", "p-arjun"],
    updatedMinsAgo: 3 * DAY_MINS,
    attention: 0,
    modules: {
      site: ["Add location", "neutral"],
      design: ["Not started", "neutral"],
      studio: ["2 open", "neutral"],
    },
  },
  {
    id: "gachibowli-tower",
    name: "Gachibowli Office Tower",
    client: "Skyline Infra",
    type: "A3",
    city: "Hyderabad",
    state: "Telangana",
    address: "Financial District, Gachibowli",
    plotAreaM2: 8_900,
    stageIndex: 4,
    health: { state: "on_track" },
    next: { title: "Working drawings issue", inDays: 11 },
    team: ["p-vikram", "p-rohan", "p-kabir"],
    updatedMinsAgo: 7 * 60,
    attention: 1,
    modules: {
      site: ["Ready", "pass"],
      design: ["Ready", "pass"],
      render: ["5", "neutral"],
      studio: ["7 open", "neutral"],
    },
  },
  {
    id: "assagao-villa",
    name: "Assagao Villa",
    client: "D'Souza family",
    type: "A1",
    city: "Assagao",
    state: "Goa",
    plotAreaM2: 1_600,
    stageIndex: 6,
    health: { state: "on_track" },
    next: { title: "Site visit: roof slab", inDays: 1 },
    team: ["p-priya", "p-asha"],
    updatedMinsAgo: 30 * 60,
    attention: 1,
    modules: {
      site: ["Ready", "pass"],
      design: ["Issued", "pass"],
      render: ["9", "neutral"],
      studio: ["3 open", "neutral"],
    },
  },
  {
    id: "filter-kaapi-cafes",
    name: "Filter Kaapi Cafés",
    client: "Filter Kaapi Co.",
    type: "I4",
    city: "Chennai",
    state: "Tamil Nadu",
    stageIndex: 6,
    health: { state: "completed" },
    team: ["p-meera"],
    updatedMinsAgo: 60 * DAY_MINS,
    archived: true,
    attention: 0,
    modules: {
      design: ["Issued", "pass"],
      render: ["14", "neutral"],
      studio: ["Closed", "neutral"],
    },
  },
  {
    id: "sample-villa",
    name: "Sample: Villa in Ahmedabad",
    client: "Orthos sample",
    type: "A1",
    city: "Ahmedabad",
    state: "Gujarat",
    plotAreaM2: 900,
    stageIndex: 3,
    sample: true,
    health: { state: "on_track" },
    next: { title: "Try issuing a sheet set", inDays: 7 },
    team: ["p-asha"],
    updatedMinsAgo: 2 * DAY_MINS,
    attention: 0,
    modules: {
      site: ["Ready", "pass"],
      design: ["Ready", "pass"],
      render: ["3", "neutral"],
      studio: ["Sample", "neutral"],
    },
  },
];
